/**
 * Server-Sent Events → TanStack Query invalidation (docs/08 §1, §7).
 *
 * `/api/events` sends `event: invalidate` with `{"entities":[...]}` after every mutation; the
 * queries whose key starts with an entity are refreshed. When the stream drops, it reconnects with
 * exponential backoff (1 s → 30 s, with jitter) and, while it is down, the active queries are
 * refreshed every 30 s (polling fallback). After a reconnection everything is refreshed once,
 * because events may have been missed.
 */
import { invalidateEventSchema } from 'core';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { API_BASE } from './api';

export type ServerEventsState = 'connecting' | 'open' | 'closed';

export const SSE_POLL_INTERVAL_MS = 30_000;
const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

/** Delay before reconnection attempt `attempt` (0-based): 1, 2, 4, 8, 16, 30, 30… s (±20%). */
export function reconnectDelay(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** Math.max(0, attempt));
  return Math.round(base * (0.8 + 0.4 * random()));
}

/** Entities of an `invalidate` event; empty when the payload is not valid. */
export function parseInvalidate(data: string): string[] {
  try {
    const result = invalidateEventSchema.safeParse(JSON.parse(data));
    return result.success ? result.data.entities : [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------- connection state (for the UI)

let state: ServerEventsState = 'connecting';
const stateListeners = new Set<() => void>();

function setState(next: ServerEventsState) {
  if (state === next) return;
  state = next;
  for (const l of stateListeners) l();
}

export function useServerEventsState(): ServerEventsState {
  return useSyncExternalStore(
    (cb) => {
      stateListeners.add(cb);
      return () => stateListeners.delete(cb);
    },
    () => state,
    () => 'closed' as const,
  );
}

export interface ServerEventsOptions {
  url?: string;
  /** Injected in tests. */
  createSource?: (url: string) => EventSource;
  pollIntervalMs?: number;
}

/** Opens the stream; returns the function that closes it (and stops reconnecting and polling). */
export function connectServerEvents(
  queryClient: QueryClient,
  options: ServerEventsOptions = {},
): () => void {
  const url = options.url ?? `${API_BASE}/events`;
  const createSource =
    options.createSource ??
    (typeof EventSource === 'undefined'
      ? null
      : (u: string) => new EventSource(u, { withCredentials: true }));
  const pollInterval = options.pollIntervalMs ?? SSE_POLL_INTERVAL_MS;

  let source: EventSource | null = null;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let stopped = false;
  let everOpened = false;

  const refreshActive = () => void queryClient.invalidateQueries({ refetchType: 'active' });

  const startPolling = () => {
    if (pollTimer == null) pollTimer = setInterval(refreshActive, pollInterval);
  };
  const stopPolling = () => {
    if (pollTimer != null) clearInterval(pollTimer);
    pollTimer = null;
  };

  const onInvalidate = (event: MessageEvent<string>) => {
    for (const entity of parseInvalidate(event.data)) {
      void queryClient.invalidateQueries({ queryKey: [entity] });
    }
  };

  const open = () => {
    if (stopped || !createSource) return;
    // 'connecting' only for the first connection; while reconnecting it stays 'closed'
    if (!everOpened && attempt === 0) setState('connecting');
    const es = createSource(url);
    source = es;
    es.addEventListener('invalidate', onInvalidate as EventListener);
    es.onopen = () => {
      if (source !== es) return;
      // events sent while disconnected are lost: refresh what is on screen once
      if (everOpened || attempt > 0) refreshActive();
      everOpened = true;
      attempt = 0;
      stopPolling();
      setState('open');
    };
    es.onerror = () => {
      if (source !== es) return;
      // the browser would retry on its own with a fixed delay: take over with backoff
      es.close();
      source = null;
      setState('closed');
      startPolling();
      if (stopped) return;
      reconnectTimer = setTimeout(open, reconnectDelay(attempt++));
    };
  };

  if (!createSource) {
    setState('closed');
    startPolling();
  } else {
    open();
  }

  // a tablet waking up: reconnect at once instead of waiting for the backoff
  const onWake = () => {
    if (stopped || source || !createSource) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (reconnectTimer != null) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    open();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onWake);
    document.addEventListener('visibilitychange', onWake);
  }

  return () => {
    stopped = true;
    if (reconnectTimer != null) clearTimeout(reconnectTimer);
    stopPolling();
    if (source) {
      source.removeEventListener('invalidate', onInvalidate as EventListener);
      source.close();
      source = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onWake);
      document.removeEventListener('visibilitychange', onWake);
    }
    setState('closed');
  };
}

/** Keeps the SSE connection open while the component is mounted (AppShell, logged-in users). */
export function useServerEvents(): void {
  const queryClient = useQueryClient();
  useEffect(() => connectServerEvents(queryClient), [queryClient]);
}
